"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ArrowLeft, Home } from "lucide-react";

/**
 * Full-viewport 404 so nested portal layouts (sidebars) never show through.
 */
export default function NotFoundView() {
  const router = useRouter();

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col overflow-hidden bg-[#f4f7fb] font-sora text-slate-800 antialiased"
      role="main"
      aria-labelledby="not-found-title"
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes nf-fade-up {
              from { opacity: 0; transform: translateY(16px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @keyframes nf-float {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-6px); }
            }
            @keyframes nf-dash {
              to { stroke-dashoffset: -24; }
            }
            .nf-stage { animation: nf-fade-up 0.55s ease-out both; }
            .nf-stage-delay { animation: nf-fade-up 0.55s ease-out 0.12s both; }
            .nf-stage-delay-2 { animation: nf-fade-up 0.55s ease-out 0.22s both; }
            .nf-float { animation: nf-float 4.5s ease-in-out infinite; }
            .nf-path { stroke-dasharray: 6 6; animation: nf-dash 1.2s linear infinite; }
            @media (prefers-reduced-motion: reduce) {
              .nf-stage, .nf-stage-delay, .nf-stage-delay-2, .nf-float, .nf-path {
                animation: none !important;
              }
            }
          `,
        }}
      />

      {/* Atmosphere */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 80% 55% at 50% -10%, rgba(43, 84, 126, 0.12), transparent 55%), radial-gradient(ellipse 50% 40% at 90% 80%, rgba(0, 112, 243, 0.06), transparent 50%)",
        }}
      />
      <svg
        aria-hidden
        className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.35]"
      >
        <defs>
          <pattern
            id="nf-grid"
            width="48"
            height="48"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M.5 48V.5H48"
              fill="none"
              stroke="#94a3b8"
              strokeOpacity="0.35"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#nf-grid)" />
      </svg>

      <header className="relative z-10 flex items-center justify-between px-6 py-5 sm:px-10">
        <Link href="/" className="inline-flex items-center gap-2">
          <Image
            src="/instacks-logo.png"
            alt="Instacks"
            width={120}
            height={36}
            className="h-8 w-auto"
            priority
          />
        </Link>
        <span className="hidden text-xs font-medium tracking-wide text-slate-400 sm:inline">
          Learning platform
        </span>
      </header>

      <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 pb-16 pt-4 text-center">
        {/* Broken learning stack — signature */}
        <div className="nf-stage nf-float mb-8" aria-hidden>
          <svg
            width="168"
            height="140"
            viewBox="0 0 168 140"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mx-auto"
          >
            <rect
              x="28"
              y="12"
              width="112"
              height="22"
              rx="6"
              fill="#2B547E"
              opacity="0.9"
            />
            <path
              className="nf-path"
              d="M84 34 V52"
              stroke="#0070f3"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            <rect
              x="40"
              y="54"
              width="88"
              height="22"
              rx="6"
              fill="none"
              stroke="#94a3b8"
              strokeWidth="2"
              strokeDasharray="5 5"
              opacity="0.7"
            />
            <path
              className="nf-path"
              d="M84 76 V94"
              stroke="#0070f3"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            <rect
              x="20"
              y="96"
              width="128"
              height="26"
              rx="7"
              fill="#0070f3"
              opacity="0.85"
            />
            <circle cx="84" cy="65" r="3" fill="#f97316" />
          </svg>
        </div>

        <p className="nf-stage font-space-grotesk text-[11px] font-semibold uppercase tracking-[0.22em] text-[#2B547E]">
          404
        </p>

        <h1
          id="not-found-title"
          className="nf-stage-delay mt-3 max-w-lg font-space-grotesk text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl md:text-[2.75rem] md:leading-[1.15]"
        >
          This path isn&apos;t on the map
        </h1>

        <p className="nf-stage-delay mt-4 max-w-md text-[15px] leading-relaxed text-slate-500">
          The page you&apos;re looking for may have moved, been renamed, or never
          existed. Head home or go back to where you left off.
        </p>

        <div className="nf-stage-delay-2 mt-9 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-center">
          <Link
            href="/"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#2B547E] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#234567] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2B547E]"
          >
            <Home className="h-4 w-4" aria-hidden />
            Back to home
          </Link>
          <button
            type="button"
            onClick={() => router.back()}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Go back
          </button>
        </div>
      </div>

      <footer className="relative z-10 px-6 pb-6 text-center text-xs text-slate-400">
        © {new Date().getFullYear()} Instacks
      </footer>
    </div>
  );
}
